import compact from 'lodash/compact'
import isBoolean from 'lodash/isBoolean'
import moment from 'moment-timezone'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import {
  IOtherShipperShipmentIdentifiers,
  IRescheduleReasons,
  IShipmentStopAppointmentStatuses,
  IShipmentStopAppointmentTypes,
  IUpdateShipmentStopInput,
  Maybe,
  IFacilityScheduleHour,
  IFacilityScheduleRulesSchedulingMethodsTypes,
} from 'types/graphqlTypes'
import { ShipperFacilityHour } from 'apps/marketplace/apiTypes/shipperFacilities'
import { TDay } from 'apps/marketplace/pages/facilities/utils/getFacilityHours'
import { validateEmail } from 'utils/validators'
import { FORMATS } from 'constants/datetime'
import { RESCHEDULE_REASON_OPTIONS } from '../ShipmentProgress/EditAppointments/utils'
import { TEditAppointmentSchemaType, TEditAppointmentsSchemaType } from './EditAppointmentSchema'
import { IStopFacilityScheduleHoursFragment } from './graphql/ScheduleHoursFragment'
import { IStopFragment } from './graphql/ShipmentStopFragment'
import { IStopShipperFacilityFragment } from './graphql/StopShipperFacility'

const createScheduleDay = (args: Partial<IStopFacilityScheduleHoursFragment>) => ({
  id: '',
  startTime: '',
  endTime: '',
  closed: false,
  ...args,
})

const getListWithMissingDays = (
  list: IStopFacilityScheduleHoursFragment[],
  stopType: 'pickup' | 'delivery',
  unloadType: 'live' | 'drop'
): IStopFacilityScheduleHoursFragment[] => {
  const filteredlist = list.filter(
    ({ unloadType: listUnloadType = '' }) => listUnloadType === unloadType
  )
  const missingDay = createScheduleDay({ unloadType, stopType })
  const listDays = filteredlist.map(item => item.day)
  const missingDays = FORMATS.fullWeekList.filter(day => !listDays.includes(day))
  const newDayArray = missingDays.map(day => ({
    day,
    ...missingDay,
  })) as IStopFacilityScheduleHoursFragment[]
  return filteredlist.concat(newDayArray)
}

export type TShipperFacilityHours = IStopShipperFacilityFragment['hours']

interface IGenerateFacilityDaySchedulesReturn {
  pickup: {
    live: NonNullable<TShipperFacilityHours['value']>
    drop: NonNullable<TShipperFacilityHours['value']>
  }
  delivery: {
    live: NonNullable<TShipperFacilityHours['value']>
    drop: NonNullable<TShipperFacilityHours['value']>
  }
}

export const generateFacilityDaySchedules = (
  hours: TShipperFacilityHours
): IGenerateFacilityDaySchedulesReturn => {
  const pickupHours = hours?.value?.filter(({ stopType }) => stopType === 'pickup')
  const pickupLiveHours = pickupHours ? getListWithMissingDays(pickupHours, 'pickup', 'live') : []
  const pickupDropHours = pickupHours ? getListWithMissingDays(pickupHours, 'pickup', 'drop') : []

  const deliveryHours = hours?.value?.filter(({ stopType = '' }) => stopType === 'delivery')
  const deliveryLiveHours = deliveryHours
    ? getListWithMissingDays(deliveryHours, 'delivery', 'live')
    : []
  const deliveryDropHours = deliveryHours
    ? getListWithMissingDays(deliveryHours, 'delivery', 'drop')
    : []

  return {
    pickup: {
      live: pickupLiveHours,
      drop: pickupDropHours,
    },
    delivery: {
      live: deliveryLiveHours,
      drop: deliveryDropHours,
    },
  }
}

export const convertToShipperHours = (hours: IFacilityScheduleHour[]): ShipperFacilityHour => {
  return {
    overridden: false,
    value: {
      unloadType: 'live',
      stopType: 'pickup',
      open247: hours.every(i => i.startTime === '00:00' && i.endTime === '23:59'),
      sameHoursMonFri: hours
        .filter(hour => hour.day !== 'sunday' && hour.day !== 'saturday')
        .map(hour => ({ start: hour.startTime, end: hour.endTime }))
        .every(time => time.start === hours[0].startTime && time.end === hours[0].endTime),
      shifts: hours.map(i => ({
        day: i.day as TDay,
        startTime: i.startTime || undefined,
        endTime: i.endTime || undefined,
        closed: i.closed,
      })),
    },
  }
}

export const getEdiShipmentOtherIds = (ids: Maybe<IOtherShipperShipmentIdentifiers[]>): string => {
  return ids?.map(({ id, idType }) => (idType ? `${idType}: ${id}` : id)).join(', ') || ''
}

export const isTimeDifferent = (
  date: Maybe<string>,
  inputTime: Maybe<string>,
  timezone?: Maybe<string>
): boolean => {
  if (!date && !inputTime) return false
  if (!date) return true
  if (!inputTime) return true

  const stopDate = moment(date)
    .tz(timezone || '')
    ?.format(FORMATS.militaryTimeFormat)

  return stopDate !== inputTime
}

export const dateTimeISO8601Timezone = (inputDateTime: string, timezone: string) => {
  // Avoids moment localization
  const inputDateTimeISO = new Date(inputDateTime).toISOString()

  return moment.tz(inputDateTimeISO, timezone).format(FORMATS.iso8601FormatTimezone)
}

const getDateFormat = (date: string, timezone: Maybe<string>): string =>
  moment(date)
    .tz(timezone || '')
    ?.format(FORMATS.dateFormat)

export const isDateDifferent = (
  date: Maybe<string>,
  inputDate: Maybe<string>,
  timezone?: Maybe<string>
): boolean => {
  if (!inputDate && !date) {
    return false
  }

  if (!inputDate || !date) {
    return true
  }

  const inputDateFormatted = getDateFormat(inputDate, timezone)
  const dateFormatted = getDateFormat(date, timezone)

  return dateFormatted !== inputDateFormatted
}

interface IIsDateTimeChanged {
  dateTime: Maybe<string>
  timezone?: string
  inputTime?: Maybe<string>
  inputDate?: Maybe<string>
}

export const isInputDateDifferent = ({
  dateTime,
  inputDate,
  inputTime,
  timezone = FORMATS.defaultTimeZone,
}: IIsDateTimeChanged) => {
  const isInputTimeDifferent = isTimeDifferent(dateTime, inputTime, timezone)
  const isInputDateDifferent = isDateDifferent(dateTime, inputDate, timezone)

  return isInputTimeDifferent || isInputDateDifferent
}

export const rescheduleReasonsArray: TSelectOpt[] = RESCHEDULE_REASON_OPTIONS

const getAppointmentStatus = (
  status: boolean | undefined,
  currentStatus?: Maybe<IShipmentStopAppointmentStatuses>
): IShipmentStopAppointmentStatuses | undefined => {
  if (!isBoolean(status)) {
    return status
  }

  if (status) {
    return IShipmentStopAppointmentStatuses.confirmed
  }

  if (currentStatus === IShipmentStopAppointmentStatuses.confirmed) {
    return IShipmentStopAppointmentStatuses.unconfirmed
  }

  return currentStatus || IShipmentStopAppointmentStatuses.unconfirmed
}

export const getUpdateShipmentRequest = (
  stop: Maybe<IStopFragment>,
  formInput: Partial<TEditAppointmentSchemaType>
): IUpdateShipmentStopInput => {
  const { appointment, id = '' } = stop || {}
  const { endAt, startAt, timezone = FORMATS.defaultTimeZone, status: currentStatus } =
    appointment || {}

  const { confirmed } = formInput || {}
  const appointmentStatus = getAppointmentStatus(confirmed, currentStatus)

  const rescheduleReason = formInput.rescheduleReason as IRescheduleReasons

  const input: IUpdateShipmentStopInput = {
    id,
    dropTrailer: formInput.dropTrailer,
    specialInstruction: formInput.specialInstruction || '',
    weight: formInput.weight,
    palletCount: formInput.palletCount,
    commodity: formInput.commodity,
    genericCommodityId: formInput.genericCommodityId,
    poReference: formInput.poReference,
    billOfLadingReference: formInput.billOfLadingReference,
    ediAppointmentReasonCode: formInput.ediAppointmentReasonCode,
    ediStatusReasonCode: formInput.ediStatusReasonCode,
    shipperFacilityUuid: formInput.facility?.id,
    appointmentInput: {
      isWorkIn: formInput.isWorkIn,
      rescheduleReason: rescheduleReason as IRescheduleReasons | undefined,
      appointmentReference: formInput.appointmentReference || '',
      confirmationReference: formInput.confirmationReference || '',
      appointmentType: formInput.appointmentType as IShipmentStopAppointmentTypes | undefined,
      status: appointmentStatus,
      startAtTime: undefined,
      startAtDate: undefined,
      endAtTime: undefined,
      endAtDate: undefined,
    },
  }

  const isStartAtTimeDifferent = isTimeDifferent(startAt, formInput.startAtTime, timezone)
  const isStartAtDateDifferent = isDateDifferent(startAt, formInput.startAtDate, timezone)
  const isEndAtTimeDifferent = isTimeDifferent(endAt, formInput.endAtTime, timezone)
  const isEndAtDateDifferent = isDateDifferent(endAt, formInput.endAtDate, timezone)

  if (!input.appointmentInput) {
    return input
  }

  if (isStartAtTimeDifferent) {
    input.appointmentInput.startAtTime = formInput.startAtTime || null
  }

  if (isStartAtDateDifferent) {
    input.appointmentInput.startAtDate = formInput.startAtDate
      ? dateTimeISO8601Timezone(formInput.startAtDate, timezone)
      : null
  }

  if (formInput.appointmentType === 'range') {
    if (isEndAtTimeDifferent) {
      input.appointmentInput.endAtTime = formInput.endAtTime || null
    }
    if (isEndAtDateDifferent) {
      input.appointmentInput.endAtDate = formInput.endAtDate
        ? dateTimeISO8601Timezone(formInput.endAtDate, timezone)
        : null
    }
  }

  if (rescheduleReason) {
    input.appointmentInput.status = getAppointmentStatus(confirmed)
  }

  return input
}

export const getSchedulerHasEmail = (stop?: IStopFragment): boolean => {
  const { mergedFacility, stopType } = stop || {}
  const { contacts: mergedContacts = [] } = mergedFacility || {}
  const contacts = mergedContacts.flatMap(({ value }) => compact(value))
  const scheduler = contacts?.find(({ contactMethods = [], contactRoles = [], canScheduleFor }) => {
    const emails = compact(
      contactMethods
        .filter(item => item.contactMethod === 'email')
        .map(item => item.value)
        .join()
        .split(',')
        .map(item => item.trim())
    )
    if (!emails.length) return false

    return (
      contactRoles?.includes('scheduling') &&
      emails.every(email => validateEmail(email.trim() || '')) &&
      ['both', stopType].includes(canScheduleFor || '')
    )
  })
  return Boolean(scheduler)
}

export const getIsFacilitySchedulingMethodContact = (stop?: IStopFragment): boolean => {
  const { dropTrailer, mergedFacility, stopType } = stop || {}
  const { schedule } = mergedFacility || {}
  const thisUnloadType = dropTrailer ? 'DROP' : 'LIVE'

  return Boolean(
    schedule?.rules.value.some(
      ({ schedulingMethod, stopType: ruleStopType, unloadType }) =>
        ruleStopType === stopType &&
        schedulingMethod !== IFacilityScheduleRulesSchedulingMethodsTypes.TMS &&
        unloadType === thisUnloadType
    )
  )
}

export const getIsAppointmentRequestValid = (stop?: IStopFragment): boolean => {
  const { appointment, autoAppointmentSchedule } = stop || {}
  const { startAt } = appointment || {}
  const { appointmentRequest } = autoAppointmentSchedule || {}

  return !appointmentRequest || appointmentRequest !== startAt
}

export const getIsStatusUnconfirmed = (stop?: IStopFragment): boolean => {
  const { status } = stop?.appointment || {}

  return (
    status === IShipmentStopAppointmentStatuses.unconfirmed ||
    status === IShipmentStopAppointmentStatuses.predicted
  )
}

export const getIsIdealAppointmentTime = (
  suggestedDateTime: string | null,
  currentDateTime: string | null,
  timezone: string | null
) => {
  if (!suggestedDateTime || !currentDateTime || !timezone) {
    return false
  }
  const currentDateMoment = moment.tz(currentDateTime, timezone)
  const suggestedDateMoment = moment.tz(suggestedDateTime, timezone)
  const duration = moment.duration(currentDateMoment.diff(suggestedDateMoment))
  const hours = duration.asHours()
  return Math.abs(hours) <= 2
}

export const getShowSendRequest = (stop?: IStopFragment): boolean => {
  const isUnconfirmedStatus = getIsStatusUnconfirmed(stop)
  const isAppointmentRequestValid = getIsAppointmentRequestValid(stop)
  const schedulerHasEmail = getSchedulerHasEmail(stop)
  const isFacilitySchedulingMethodContact = getIsFacilitySchedulingMethodContact(stop)

  return Boolean(
    isUnconfirmedStatus &&
      isAppointmentRequestValid &&
      schedulerHasEmail &&
      isFacilitySchedulingMethodContact
  )
}

export const getMilitaryDateTime = (
  date: Maybe<string>,
  timezone: Maybe<string>
): string | undefined => {
  if (!date) {
    return undefined
  }

  return moment.tz(date, timezone || '').format(FORMATS.militaryTimeFormat)
}

export const getDefaultValues = (
  stops: Maybe<Maybe<IStopFragment>[]>
): Partial<TEditAppointmentsSchemaType> => {
  if (!stops) {
    return {}
  }

  return {
    shipmentStops: stops.map(stop => {
      const { appointment } = stop || {}
      const { endAt, endAtHasTime, startAt, startAtHasTime, timezone = FORMATS.defaultTimeZone } =
        appointment || {}
      const startAtTime = startAt && startAtHasTime ? getMilitaryDateTime(startAt, timezone) : null
      const endAtTime = endAt && endAtHasTime ? getMilitaryDateTime(endAt, timezone) : null

      return {
        startAtDate: startAt || undefined,
        startAtTime,
        endAtDate: endAt || undefined,
        endAtTime,
      }
    }),
  }
}

export const formatEmailList = (emails: string): string => {
  return emails.replaceAll(/(\s*[;,]\s*)|\s+/g, '; ')
}
